import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import CommonPageHeader from '../components/common/CommonPageHeader';
import LoadingScreen from '../components/common/LoadingScreen';
import { 
  BugAntIcon,
  FunnelIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  PlayIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { collection, query, where, orderBy, limit, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';

interface FlowExecution {
  id: string;
  flowId: string;
  flowName: string;
  status: 'running' | 'completed' | 'failed' | 'cancelled' | 'unknown';
  startedAt: Date;
  completedAt?: Date;
  duration?: number;
  triggeredBy?: string;
  triggerType?: string;
  organizationId: string;
  error?: string;
  executionLog?: any[];
  completedNodes?: string[];
  failedNodes?: string[];
  variables?: any;
}

interface NodeExecution {
  nodeId: string;
  nodeName: string;
  nodeType: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
  startTime?: Date;
  endTime?: Date;
  duration?: number;
  input?: any;
  output?: any;
  error?: string;
}

const FlowDebug: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [executions, setExecutions] = useState<FlowExecution[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedExecution, setSelectedExecution] = useState<FlowExecution | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState({
    status: '',
    flowId: '',
    dateRange: 'today'
  });

  useEffect(() => {
    loadExecutions();
  }, [user, filters]);

  const loadExecutions = async () => {
    if (!user?.organizationId) return;

    try {
      setLoading(true);
      const executionsRef = collection(db, 'flowExecutions');
      
      // Try with createdAt first (this field always exists)
      let q = query(
        executionsRef,
        where('organizationId', '==', user.organizationId),
        orderBy('createdAt', 'desc'),
        limit(100)
      );

      const snapshot = await getDocs(q);
      // console.log('📊 Flow executions found:', snapshot.docs.length);
      
      // Get unique flow IDs
      const flowIds = Array.from(new Set(snapshot.docs.map(doc => doc.data().flowId).filter(Boolean)));
      
      // Fetch flow names
      const flowNames: Record<string, string> = {};
      for (const flowId of flowIds) {
        try {
          const flowDoc = await getDocs(
            query(collection(db, 'flows'), where('__name__', '==', flowId), limit(1))
          );
          if (!flowDoc.empty) {
            flowNames[flowId] = flowDoc.docs[0].data().name || flowId;
          }
        } catch (error) {
          console.error('Error fetching flow name:', flowId, error);
        }
      }
      
      const executionsList = snapshot.docs.map(doc => {
        const data = doc.data();
        // console.log('📄 Execution data:', doc.id, data);
        
        return {
          id: doc.id,
          flowId: data.flowId || '',
          flowName: flowNames[data.flowId] || data.flowName || 'Unknown Flow',
          status: data.status || 'unknown',
          startedAt: (data.startedAt?.toDate?.() || data.createdAt?.toDate?.() || new Date()),
          completedAt: data.completedAt?.toDate?.(),
          duration: data.duration,
          triggeredBy: data.triggeredBy || data.userId,
          triggerType: data.triggerType || 'manual',
          organizationId: data.organizationId,
          error: data.error || data.errorMessage,
          executionLog: data.executionLog || [],
          completedNodes: data.completedNodes || [],
          failedNodes: data.failedNodes || [],
          variables: data.variables || {},
        } as FlowExecution;
      });

      // console.log('✅ Processed executions:', executionsList.length);
      setExecutions(executionsList);
    } catch (error) {
      console.error('❌ Error loading flow executions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'running':
        return <PlayIcon className="h-5 w-5 text-blue-500 animate-pulse" />;
      case 'cancelled':
        return <XCircleIcon className="h-5 w-5 text-gray-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      completed: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
      failed: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
      running: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
      cancelled: 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400',
    };
    return badges[status as keyof typeof badges] || badges.cancelled;
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(2)}s`;
    return `${(ms / 60000).toFixed(2)}m`;
  };

  const formatDate = (date?: Date | any) => {
    if (!date) return '-';
    
    // Handle Firestore Timestamp
    let dateObj = date;
    if (date?.toDate && typeof date.toDate === 'function') {
      dateObj = date.toDate();
    } else if (typeof date === 'string') {
      dateObj = new Date(date);
    } else if (!(date instanceof Date)) {
      return '-';
    }
    
    return new Intl.DateTimeFormat('hu-HU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).format(dateObj);
  };

  const toggleNodeExpansion = (nodeId: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <CommonPageHeader
        title={t('nav.flowDebug')}
        subtitle={t('nav.flowDebugSubtitle')}
        icon={BugAntIcon}
      >
        <button
          onClick={loadExecutions}
          className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors"
        >
          <ArrowPathIcon className="h-4 w-4 mr-2" />
          <span>{t('common.refresh')}</span>
        </button>
      </CommonPageHeader>

      <div className="w-full px-4 py-4">
        {/* Filters */}
        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
            <FunnelIcon className="h-5 w-5 mr-2" />
            Szűrők
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-white/90 mb-2">
                Státusz
              </label>
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="w-full border-0 rounded-lg px-3 py-2 text-sm bg-white/20 backdrop-blur-sm text-white placeholder-white/60 focus:bg-white/30 focus:ring-2 focus:ring-white/50"
              >
                <option value="" className="bg-gray-800 text-white">Minden</option>
                <option value="completed" className="bg-gray-800 text-white">Sikeres</option>
                <option value="failed" className="bg-gray-800 text-white">Sikertelen</option>
                <option value="running" className="bg-gray-800 text-white">Futó</option>
                <option value="cancelled" className="bg-gray-800 text-white">Megszakítva</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/90 mb-2">
                Időszak
              </label>
              <select
                value={filters.dateRange}
                onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
                className="w-full border-0 rounded-lg px-3 py-2 text-sm bg-white/20 backdrop-blur-sm text-white placeholder-white/60 focus:bg-white/30 focus:ring-2 focus:ring-white/50"
              >
                <option value="today" className="bg-gray-800 text-white">Ma</option>
                <option value="yesterday" className="bg-gray-800 text-white">Tegnap</option>
                <option value="week" className="bg-gray-800 text-white">Ez a hét</option>
                <option value="month" className="bg-gray-800 text-white">Ez a hónap</option>
                <option value="all" className="bg-gray-800 text-white">Minden</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/90 mb-2">
                Eredmények
              </label>
              <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-2 text-white font-semibold">
                {executions.length} futás
              </div>
            </div>
          </div>
        </div>

        {/* Executions List */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Execution List */}
          <div className="bg-white dark:bg-gray-800/50 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="bg-gradient-to-r from-purple-500 to-pink-600 p-4">
              <h3 className="text-lg font-semibold text-white flex items-center">
                <PlayIcon className="h-5 w-5 mr-2" />
                Flow Futások
              </h3>
            </div>
            <div className="overflow-y-auto max-h-[calc(100vh-300px)]">
              {executions.length === 0 ? (
                <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                  Nincs megjeleníthető futás
                </div>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {executions.map((execution) => (
                    <div
                      key={execution.id}
                      onClick={() => setSelectedExecution(execution)}
                      className={`p-4 cursor-pointer transition-all duration-200 ${
                        selectedExecution?.id === execution.id
                          ? 'bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/30 border-l-4 border-purple-500'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:border-l-4 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(execution.status)}
                          <span className="font-medium text-gray-900 dark:text-white">
                            {execution.flowName}
                          </span>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadge(execution.status)}`}>
                          {execution.status}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                        <div className="flex items-center justify-between">
                          <span>Indítva:</span>
                          <span>{formatDate(execution.startedAt)}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>Időtartam:</span>
                          <span>{formatDuration(execution.duration)}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>Trigger:</span>
                          <span className="text-xs">{execution.triggerType}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right: Execution Details */}
          <div className="bg-white dark:bg-gray-800/50 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="bg-gradient-to-r from-green-500 to-teal-600 p-4">
              <h3 className="text-lg font-semibold text-white flex items-center">
                <DocumentTextIcon className="h-5 w-5 mr-2" />
                Futás Részletei
              </h3>
            </div>
            <div className="overflow-y-auto max-h-[calc(100vh-300px)]">
              {!selectedExecution ? (
                <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                  Válassz egy futást a részletek megtekintéséhez
                </div>
              ) : (
                <div className="p-4 space-y-4">
                  {/* Execution Info */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
                    <h4 className="font-semibold text-blue-900 dark:text-blue-300 mb-3 flex items-center">
                      <ClockIcon className="h-4 w-4 mr-2" />
                      Alapadatok
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Flow:</span>
                        <span className="text-gray-900 dark:text-white font-medium">
                          {selectedExecution.flowName}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Státusz:</span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadge(selectedExecution.status)}`}>
                          {selectedExecution.status}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Kezdés:</span>
                        <span className="text-gray-900 dark:text-white">
                          {formatDate(selectedExecution.startedAt)}
                        </span>
                      </div>
                      {selectedExecution.completedAt && (
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Befejezés:</span>
                          <span className="text-gray-900 dark:text-white">
                            {formatDate(selectedExecution.completedAt)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Időtartam:</span>
                        <span className="text-gray-900 dark:text-white font-medium">
                          {formatDuration(selectedExecution.duration)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Trigger:</span>
                        <span className="text-gray-900 dark:text-white">
                          {selectedExecution.triggerType}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Indította:</span>
                        <span className="text-gray-900 dark:text-white">
                          {selectedExecution.triggeredBy}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Error Message */}
                  {selectedExecution.error && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                      <h4 className="font-medium text-red-900 dark:text-red-400 mb-2">
                        Hiba
                      </h4>
                      <p className="text-sm text-red-800 dark:text-red-300">
                        {selectedExecution.error}
                      </p>
                    </div>
                  )}

                  {/* Execution Log */}
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-4 border border-purple-200 dark:border-purple-800">
                    <h4 className="font-semibold text-purple-900 dark:text-purple-300 mb-3 flex items-center">
                      <DocumentTextIcon className="h-4 w-4 mr-2" />
                      Execution Log ({selectedExecution.executionLog?.length || 0})
                    </h4>
                    <div className="space-y-2">
                      {selectedExecution.executionLog?.map((logEntry: any, index: number) => (
                        <div
                          key={index}
                          className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-600 transition-colors"
                        >
                          <div
                            onClick={() => toggleNodeExpansion(String(index))}
                            className="p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                {expandedNodes.has(String(index)) ? (
                                  <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                                ) : (
                                  <ChevronRightIcon className="h-4 w-4 text-gray-500" />
                                )}
                                <DocumentTextIcon className="h-4 w-4 text-gray-500" />
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                  {index + 1}. {logEntry.node_type || logEntry.message || 'Log Entry'}
                                </span>
                                <span className={`text-xs px-2 py-0.5 rounded ${
                                  logEntry.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                                  logEntry.status === 'failed' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                                  'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                }`}>
                                  {logEntry.status || logEntry.level || 'info'}
                                </span>
                              </div>
                              <span className="text-xs text-gray-600 dark:text-gray-400">
                                {formatDate(logEntry.timestamp)}
                              </span>
                            </div>
                          </div>

                          {expandedNodes.has(String(index)) && (
                            <div className="px-3 pb-3 space-y-2 border-t border-gray-200 dark:border-gray-700 pt-2">
                              {/* Node Info */}
                              {logEntry.node_id && (
                                <div className="text-xs text-gray-600 dark:text-gray-300">
                                  <span className="font-medium text-gray-700 dark:text-gray-200">Node ID:</span> {logEntry.node_id}
                                </div>
                              )}
                              
                              {/* Error */}
                              {logEntry.error && (
                                <div className="bg-red-50 dark:bg-red-900/20 rounded p-2">
                                  <p className="text-xs font-medium text-red-900 dark:text-red-300 mb-1">Error:</p>
                                  <p className="text-xs text-red-800 dark:text-red-300">
                                    {logEntry.error}
                                  </p>
                                </div>
                              )}
                              
                              {/* Input Data */}
                              {logEntry.input_data && Object.keys(logEntry.input_data).length > 0 && (
                                <div>
                                  <p className="text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
                                    Input:
                                  </p>
                                  <pre className="text-xs bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded p-2 overflow-x-auto max-h-40">
                                    {JSON.stringify(logEntry.input_data, null, 2)}
                                  </pre>
                                </div>
                              )}
                              
                              {/* Output Data */}
                              {logEntry.output_data && Object.keys(logEntry.output_data).length > 0 && (
                                <div>
                                  {/* Bulk SMS/Email Summary */}
                                  {logEntry.output_data.bulk_mode && (
                                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 mb-2">
                                      <p className="text-xs font-semibold text-blue-900 dark:text-blue-300 mb-2">
                                        📊 Tömeges Küldés Eredmény
                                      </p>
                                      <div className="grid grid-cols-2 gap-2 text-xs">
                                        <div>
                                          <span className="text-blue-700 dark:text-blue-400">Összes:</span>
                                          <span className="ml-1 font-medium text-blue-900 dark:text-blue-200">
                                            {logEntry.output_data.total_recipients || 0}
                                          </span>
                                        </div>
                                        <div>
                                          <span className="text-green-700 dark:text-green-400">Sikeres:</span>
                                          <span className="ml-1 font-medium text-green-900 dark:text-green-200">
                                            {logEntry.output_data.success_count || 0}
                                          </span>
                                        </div>
                                        <div>
                                          <span className="text-red-700 dark:text-red-400">Sikertelen:</span>
                                          <span className="ml-1 font-medium text-red-900 dark:text-red-200">
                                            {logEntry.output_data.failed_count || 0}
                                          </span>
                                        </div>
                                        <div>
                                          <span className="text-yellow-700 dark:text-yellow-400">Kihagyva:</span>
                                          <span className="ml-1 font-medium text-yellow-900 dark:text-yellow-200">
                                            {logEntry.output_data.skipped_count || 0}
                                          </span>
                                        </div>
                                      </div>
                                      {logEntry.output_data.success_rate !== undefined && (
                                        <div className="mt-2 pt-2 border-t border-blue-200 dark:border-blue-800">
                                          <span className="text-xs text-blue-700 dark:text-blue-400">Sikerességi arány:</span>
                                          <span className="ml-1 font-semibold text-blue-900 dark:text-blue-200">
                                            {logEntry.output_data.success_rate.toFixed(1)}%
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  
                                  <p className="text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
                                    Output:
                                  </p>
                                  <pre className="text-xs bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded p-2 overflow-x-auto max-h-40">
                                    {JSON.stringify(logEntry.output_data, null, 2)}
                                  </pre>
                                </div>
                              )}
                              
                              {/* Generic Data fallback */}
                              {logEntry.data && (
                                <div>
                                  <p className="text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
                                    Data:
                                  </p>
                                  <pre className="text-xs bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded p-2 overflow-x-auto max-h-40">
                                    {JSON.stringify(logEntry.data, null, 2)}
                                  </pre>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlowDebug;
